import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, ExternalLink, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/context/SeoContext";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/data/content";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://www.perthtilingwa.com.au").replace(/\/$/, "");
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_KEYS = [
  { key: "home", label: "Home" },
  ...CATEGORIES.map((c) => ({ key: c.id, label: c.title })),
];

const inputClass =
  "w-full bg-[#0f0f11] border border-white/15 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 px-4 py-2.5 text-sm outline-none transition-colors";

function Field({ label, value, onChange, placeholder, textarea, testid, hint }) {
  return (
    <div>
      <label className="overline text-stone-500 block mb-2">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputClass} resize-none`} />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputClass} />
      )}
      {hint && <p className="text-xs text-stone-600 mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminSeo() {
  const { user, ready, logout } = useAuth();
  const { seo, refresh } = useSeo();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [verify, setVerify] = useState({ google: "" });
  const [pages, setPages] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  useEffect(() => {
    if (seo) {
      setBiz({
        ...seo.business,
        area_served: (seo.business.area_served || []).join(", "),
        same_as: (seo.business.same_as || []).join(", "),
      });
      setVerify(seo.verification || { google: "" });
      setPages(JSON.parse(JSON.stringify(seo.pages || {})));
    }
  }, [seo]);

  const setBizField = (k) => (v) => setBiz((b) => ({ ...b, [k]: v }));
  const setPageField = (pk, field) => (v) =>
    setPages((p) => ({ ...p, [pk]: { ...p[pk], [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const business = {
        ...biz,
        area_served: biz.area_served.split(",").map((s) => s.trim()).filter(Boolean),
        same_as: biz.same_as.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/seo", { business, verification: verify, pages });
      await refresh();
      toast.success("SEO settings saved.");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !user || !biz) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="admin-seo">
      <div className="grain-overlay" aria-hidden />

      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/logo.png" alt="Perth Tiling WA" className="h-10 w-auto object-contain" />
            <span className="hidden sm:block overline text-stone-500">SEO Settings</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="back-to-enquiries" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Enquiries
            </Link>
            <button data-testid="seo-save-top" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#d4c4b7] text-[#0a0a0c] hover:bg-white disabled:opacity-60 px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10 space-y-14">
        <div>
          <h1 className="font-serif font-light text-stone-50 text-4xl md:text-5xl mb-3">SEO & Local Search</h1>
          <p className="text-stone-400 font-light max-w-2xl">Edit your business details, page titles and descriptions. These power your Google structured data, page meta tags and social previews.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href={`${SITE_URL}/sitemap.xml`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[#d4c4b7] border border-white/15 px-4 py-2 transition-colors">
              <ExternalLink className="h-3.5 w-3.5" /> Live sitemap.xml
            </a>
            <a href={`${API_BASE}/sitemap.xml`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[#d4c4b7] border border-white/15 px-4 py-2 transition-colors">
              <ExternalLink className="h-3.5 w-3.5" /> Dynamic sitemap (API)
            </a>
            <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[#d4c4b7] border border-white/15 px-4 py-2 transition-colors">
              <Search className="h-3.5 w-3.5" /> Google Search Console
            </a>
          </div>
        </div>

        {/* Business */}
        <section data-testid="seo-business">
          <p className="overline text-[#d4c4b7] mb-6">Business Details (Local SEO)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Field label="Business Name" value={biz.name} onChange={setBizField("name")} testid="biz-name" />
            <Field label="Tagline" value={biz.tagline} onChange={setBizField("tagline")} testid="biz-tagline" />
            <Field label="Email" value={biz.email} onChange={setBizField("email")} testid="biz-email" />
            <Field label="Phone (display)" value={biz.phone_display} onChange={setBizField("phone_display")} testid="biz-phone" />
            <Field label="Phone (international)" value={biz.phone_e164} onChange={setBizField("phone_e164")} placeholder="+61424524388" hint="E.164 format for Google" testid="biz-phone-e164" />
            <Field label="Price Range" value={biz.price_range} onChange={setBizField("price_range")} placeholder="$$$" testid="biz-price" />
            <Field label="Street Address" value={biz.street} onChange={setBizField("street")} testid="biz-street" />
            <Field label="Suburb" value={biz.suburb} onChange={setBizField("suburb")} testid="biz-suburb" />
            <Field label="Region" value={biz.region} onChange={setBizField("region")} placeholder="WA" testid="biz-region" />
            <Field label="Postcode" value={biz.postcode} onChange={setBizField("postcode")} testid="biz-postcode" />
            <Field label="Country" value={biz.country} onChange={setBizField("country")} placeholder="AU" testid="biz-country" />
            <Field label="Latitude" value={biz.latitude} onChange={setBizField("latitude")} testid="biz-lat" />
            <Field label="Longitude" value={biz.longitude} onChange={setBizField("longitude")} testid="biz-lng" />
            <div className="md:col-span-2">
              <Field label="Areas Served (comma separated)" value={biz.area_served} onChange={setBizField("area_served")} hint="Suburbs / cities for local SEO" testid="biz-areas" />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <Field label="Social Profile URLs (comma separated)" value={biz.same_as} onChange={setBizField("same_as")} placeholder="https://facebook.com/... , https://instagram.com/..." hint="Used for Google 'sameAs' knowledge links" testid="biz-social" />
            </div>
          </div>
        </section>

        {/* Verification */}
        <section data-testid="seo-verification">
          <p className="overline text-[#d4c4b7] mb-6">Google Search Console Verification</p>
          <div className="max-w-2xl">
            <Field
              label="google-site-verification content value"
              value={verify.google}
              onChange={(v) => setVerify({ ...verify, google: v })}
              placeholder="paste the content value from the HTML tag method"
              hint="In Search Console → Settings → Ownership verification → HTML tag, copy only the content=... value here."
              testid="verify-google"
            />
          </div>
        </section>

        {/* Pages */}
        <section data-testid="seo-pages">
          <p className="overline text-[#d4c4b7] mb-6">Page Titles & Descriptions</p>
          <div className="space-y-8">
            {PAGE_KEYS.map(({ key, label }) => (
              <div key={key} data-testid={`seo-page-${key}`} className="border border-white/10 bg-[#0f0f11] p-6 md:p-8">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-serif text-2xl text-stone-50">{label}</h3>
                  <span className="text-xs text-stone-600">{key === "home" ? "/" : `/services/${key}`}</span>
                </div>
                <div className="grid grid-cols-1 gap-5">
                  <Field label="Meta Title" value={pages[key]?.title} onChange={setPageField(key, "title")} testid={`page-${key}-title`} hint={`${(pages[key]?.title || "").length} characters (aim 50–60)`} />
                  <Field label="Meta Description" textarea value={pages[key]?.description} onChange={setPageField(key, "description")} testid={`page-${key}-desc`} hint={`${(pages[key]?.description || "").length} characters (aim 150–160)`} />
                  <Field label="Keywords" value={pages[key]?.keywords} onChange={setPageField(key, "keywords")} testid={`page-${key}-kw`} />
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="pt-4">
          <button data-testid="seo-save-bottom" onClick={save} disabled={saving} className="inline-flex items-center gap-3 bg-[#d4c4b7] text-[#0a0a0c] hover:bg-white disabled:opacity-60 px-10 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save All Changes
          </button>
        </div>
      </main>
    </div>
  );
}
